import { useEffect, useRef, useState } from 'react';
import { REACTIONS, newestPostAt, reactionCount, type TrashPost, type Reactor } from '@ff/core';
import { fetchTalk, postTalk, deleteTalk, reactTalk, ApiError } from '../lib/api.ts';
import { useMe } from '../lib/me.tsx';
import { useUnread } from '../lib/unread.tsx';
import { Avatar } from './Avatar.tsx';
import { relativeTime } from '../lib/format.ts';

/** How often the board refreshes itself so new smack shows up without a reload. */
const POLL_MS = 15_000;

/**
 * A trash-talk board: the one place in the app the buddies write instead of
 * read. Keyed by (season, week) — a real game week under the Scoreboard, or the
 * pre-season sentinel for the War Room. `subtitle` and `emptyPrompt` let the
 * caller phrase it for its context; both default to the weekly wording.
 */
export function TrashTalk({
  season,
  week,
  subtitle,
  emptyPrompt,
  readOnly = false,
  focusPostId = null,
  focusKey = '',
}: {
  season: number;
  week: number;
  subtitle?: string;
  emptyPrompt?: string;
  /** An archived board: posts are shown, but nothing new can be said. */
  readOnly?: boolean;
  /** A post to scroll to and light up, from a tapped notification. */
  focusPostId?: string | null;
  /** Changes on every navigation, so a second tap for the same post scrolls again. */
  focusKey?: string;
}) {
  const me = useMe();
  const { markRead } = useUnread();
  const [posts, setPosts] = useState<TrashPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // Which focus request has been honoured, so polls never re-scroll.
  const focusedRef = useRef<string | null>(null);

  // A ref holds the latest week so the interval always reads the current board,
  // not a stale one captured at mount.
  const key = `${season}_${week}`;
  const keyRef = useRef(key);
  keyRef.current = key;

  // Every board update — poll or mutation — is stamped with a monotonically
  // increasing sequence number, and we only apply a result if it's the newest
  // one seen. Without this, a 15s poll that began before a post/react/delete
  // can resolve AFTER the mutation and overwrite the fresh board with the stale
  // pre-mutation one, making the just-added post flicker away until the next
  // tick. Because a mutation always draws a higher number than any poll already
  // in flight, its board wins and the late poll is dropped.
  const seqRef = useRef(0);
  const appliedRef = useRef(0);

  const apply = (next: TrashPost[], seq: number) => {
    if (seq < appliedRef.current) return; // a newer result already won
    appliedRef.current = seq;
    setPosts(next);
    setError(null); // a successful update means we're in a good state again
  };

  useEffect(() => {
    let alive = true;
    // A new board: reset both the view and the ordering counters.
    setPosts(null);
    setError(null);
    seqRef.current = 0;
    appliedRef.current = 0;

    // `initial` distinguishes the first load (whose failure must surface, or the
    // board sits on a spinner forever) from background polls (whose failures are
    // silent — the next tick recovers).
    const load = (initial: boolean) => {
      const at = keyRef.current;
      const seq = ++seqRef.current;
      fetchTalk(season, week)
        .then((p) => {
          if (alive && keyRef.current === at) apply(p, seq);
        })
        .catch(() => {
          if (alive && keyRef.current === at && initial) {
            setError("Couldn't load the board. Retrying…");
          }
        });
    };
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [season, week]);

  // Having a board on screen is having read it. Runs after the posts commit,
  // so the mark never gets ahead of what was actually rendered, and only while
  // the tab is visible, so a background poll doesn't read on the member's
  // behalf. The provider drops marks that aren't newer than the last one sent.
  useEffect(() => {
    if (!posts || document.visibilityState !== 'visible') return;
    const newest = newestPostAt(posts);
    if (newest) markRead(season, week, newest);
  }, [posts, season, week, markRead]);

  // The notification said "this post"; go there once the board holds it. A
  // request is honoured once per navigation, and counted as honoured only
  // when the post was found: this can run while `posts` still holds the
  // previous week's board (the page switches weeks in an effect of its own),
  // and burning the token on that miss would leave the right board unscrolled
  // when it loads. A post that's gone (deleted) is looked for on each poll
  // and simply never found, which costs nothing.
  useEffect(() => {
    if (!focusPostId || posts === null) return;
    const token = `${focusKey}:${focusPostId}`;
    if (focusedRef.current === token) return;
    if (!posts.some((p) => p.id === focusPostId)) return;
    focusedRef.current = token;
    document
      .getElementById(`post-${focusPostId}`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setHighlightId(focusPostId);
    const t = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(t);
  }, [posts, focusPostId, focusKey]);

  // Every mutation returns the full board, so the server stays the source of
  // truth and concurrent posts merge in. The result is stamped and applied
  // through the same sequence guard as polls, so it can't be clobbered by an
  // older in-flight poll.
  const run = (fn: () => Promise<TrashPost[]>) => {
    const at = keyRef.current;
    const seq = ++seqRef.current;
    setError(null);
    return fn()
      .then((p) => {
        if (keyRef.current === at) apply(p, seq);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Something went wrong'));
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="font-display text-lg font-black uppercase tracking-tight">Trash Talk</h2>
        <span className="font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
          {subtitle ?? `Week ${week}`}
        </span>
      </div>

      {posts === null ? (
        error ? (
          <p className="font-mono text-xs text-blood">{error}</p>
        ) : (
          <p className="font-mono text-xs text-chalk-dim">loading…</p>
        )
      ) : posts.length === 0 ? (
        <p className="font-editorial text-sm italic text-chalk-dim">
          {emptyPrompt ?? `Nobody's said a word about Week ${week}. Be the first.`}
        </p>
      ) : (
        <ul className="space-y-2">
          {posts.map((p) => (
            <PostRow
              key={p.id}
              post={p}
              myEmail={me?.email}
              readOnly={readOnly}
              highlighted={p.id === highlightId}
              canDelete={
                !readOnly && (p.authorEmail === me?.email || me?.isCommissioner === true)
              }
              onReact={(emoji) => run(() => reactTalk(season, week, p.id, emoji))}
              onDelete={() => run(() => deleteTalk(season, week, p.id))}
            />
          ))}
        </ul>
      )}

      {!readOnly && (
        <Composer onSubmit={(text) => run(() => postTalk(season, week, text))} />
      )}
      {/* The null-board case renders its own error above; here we only surface
          errors once a board is showing (a failed post/react/delete). */}
      {posts !== null && error && <p className="font-mono text-xs text-blood">{error}</p>}
    </section>
  );
}

function PostRow({
  post,
  myEmail,
  canDelete,
  readOnly,
  highlighted,
  onReact,
  onDelete,
}: {
  post: TrashPost;
  myEmail?: string;
  canDelete: boolean;
  readOnly: boolean;
  highlighted: boolean;
  onReact: (emoji: string) => void;
  onDelete: () => void;
}) {
  const [picking, setPicking] = useState(false);

  // Let a click anywhere outside the palette (or Escape) cancel it, instead of
  // trapping the user until they pick an emoji.
  const pickerRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!picking) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPicking(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPicking(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [picking]);

  // Emojis present on this post, ordered by the palette (any legacy emoji not in
  // the current palette falls to the end).
  const order = new Map<string, number>(REACTIONS.map((e, i) => [e, i]));
  const chips = Object.entries(post.reactions)
    .filter(([, who]) => who.length > 0)
    .sort((a, b) => (order.get(a[0]) ?? 99) - (order.get(b[0]) ?? 99));

  return (
    <li
      id={`post-${post.id}`}
      className={`card group flex gap-3 p-3 transition-shadow duration-700 ${
        highlighted ? 'ring-2 ring-brass' : ''
      }`}
    >
      <Avatar name={post.authorName} size={32} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-display text-sm font-bold">{post.authorName}</span>
          <span className="font-mono text-[0.65rem] text-chalk-dim">
            {relativeTime(post.createdAt)}
          </span>
          {canDelete && (
            <button
              onClick={onDelete}
              className="ml-auto font-mono text-[0.65rem] text-chalk-dim opacity-0 transition-opacity hover:text-blood focus:opacity-100 group-hover:opacity-100"
              aria-label="Delete post"
            >
              delete
            </button>
          )}
        </div>
        <p className="mt-0.5 whitespace-pre-wrap break-words font-editorial text-sm text-chalk">
          {post.text}
        </p>

        {/* An archived board shows its reactions but never adds them, so a
            post with none renders no empty reaction row at all. */}
        {(!readOnly || chips.length > 0) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {chips.map(([emoji, who]) => {
              const mine = myEmail ? who.some((r) => r.email === myEmail) : false;
              const cls = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${
                mine ? 'border-brass/60 bg-brass/15' : 'border-felt-line'
              }`;
              const label = reactorLabel(who, myEmail, emoji);
              const inner = (
                <>
                  <span className="text-sm leading-none">{emoji}</span>
                  <span
                    className={`font-mono text-xs leading-none tabular-nums ${
                      mine ? 'text-brass' : 'text-chalk-dim'
                    }`}
                  >
                    {who.length}
                  </span>
                </>
              );
              return readOnly ? (
                <span key={emoji} className={cls} title={label}>
                  {inner}
                </span>
              ) : (
                <button
                  key={emoji}
                  onClick={() => onReact(emoji)}
                  className={`${cls} transition-colors`}
                  title={label}
                >
                  {inner}
                </button>
              );
            })}

            {!readOnly &&
              (picking ? (
                <span
                  ref={pickerRef}
                  className="inline-flex max-w-[17rem] flex-wrap gap-0.5 rounded-lg border border-felt-line bg-felt-raised p-1"
                >
                  {REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => {
                        onReact(emoji);
                        setPicking(false);
                      }}
                      className="rounded px-1 py-0.5 text-lg leading-none hover:bg-felt"
                    >
                      {emoji}
                    </button>
                  ))}
                </span>
              ) : (
                <button
                  onClick={() => setPicking(true)}
                  className="rounded-full border border-felt-line px-1.5 py-0.5 font-mono text-xs text-chalk-dim hover:text-chalk"
                  aria-label="Add a reaction"
                >
                  {chips.length ? '＋' : '＋ react'}
                </button>
              ))}

            {reactionCount(post) > 0 && (
              <span className="sr-only">{reactionCount(post)} reactions</span>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * The Slack-style hover label for a reaction chip: "You and one other
 * reacted with 🔥". The current user reads as "You" and always comes first;
 * past four names it collapses to "…and N others" so a popular emoji doesn't
 * spill a wall of names into the tooltip.
 */
function reactorLabel(who: Reactor[], myEmail: string | undefined, emoji: string): string {
  const names = who
    .map((r) => ({ name: r.email === myEmail ? 'You' : r.name, me: r.email === myEmail }))
    .sort((a, b) => (a.me === b.me ? 0 : a.me ? -1 : 1))
    .map((r) => r.name);

  const MAX = 4;
  let joined: string;
  if (names.length <= MAX) {
    joined =
      names.length <= 1
        ? (names[0] ?? '')
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  } else {
    const extra = names.length - MAX;
    joined = `${names.slice(0, MAX).join(', ')} and ${extra} other${extra === 1 ? '' : 's'}`;
  }
  return `${joined} reacted with ${emoji}`;
}

function Composer({ onSubmit }: { onSubmit: (text: string) => Promise<unknown> }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const send = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    onSubmit(trimmed)
      .then(() => setText(''))
      .finally(() => setSending(false));
  };

  return (
    <div className="flex items-end gap-2">
      {/* 16px on touch screens so focusing it never zooms the page; the
          smaller size comes back on anything with a mouse. */}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends; Shift+Enter makes a new line.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        rows={1}
        maxLength={500}
        placeholder="Talk your shit…"
        className="min-h-[2.4rem] flex-1 resize-y rounded-md border border-felt-line bg-felt-raised px-3 py-2 font-editorial text-base text-chalk placeholder:text-chalk-dim focus:border-brass focus:outline-none mouse:text-sm"
      />
      <button
        onClick={send}
        disabled={sending || !text.trim()}
        className="shrink-0 rounded-md bg-brass px-4 py-2 font-mono text-xs uppercase tracking-wider text-felt transition-opacity disabled:opacity-40"
      >
        {sending ? '…' : 'Post'}
      </button>
    </div>
  );
}
